import { useEffect, useState } from 'react'
import { useConfig } from '@ynap/immutables-provider'
import { DeliveryTracking } from '@ynap/api-utils'
import { timeout } from '@ynap/promise-utils'
import { raygun } from '@ynap/clientside-monitoring'

export interface IDeliveryTrackingEvent {
    category: string
    key: string
    description: string
    date: string
    externalStatusMessage: string
    externalSubStatusMessage: string
    externalLocation: string
}

interface IDeliveryTracking {
    courier: string
    estimatedDeliveryDate: string
    events: IDeliveryTrackingEvent[]
}

const reportToRaygun = (error: any) =>
    raygun.helpers.agent('send', {
        error,
        tags: ['delivery-tracking-fetch-error'],
    })

const useDeliveryTracking = (orderId: string, trackingNumber: string, isTrackableOrder: boolean) => {
    const [deliveryInfo, setDeliveryInfo] = useState({
        courier: '',
        estimatedDeliveryDate: '',
        deliveryTrackingData: [] as IDeliveryTrackingEvent[],
        isFetchComplete: false,
    })

    const config = useConfig()
    const baseUrl = config?.head?.deliveryTrackingService?.baseUrl
    const brand = config?.global?.application?.brandId

    const handleWithResponse = (responseJson: IDeliveryTracking) => {
        // All Delivery Tracking API events are listed here: https://confluence.nap/display/RDCST/Tracked+Events+List)
        // TRANSIT_001, TRANSIT_002, TRANSIT_003, EXCEPTION (all), DELIVERED (all)
        const supportedEventsRegex = /TRANSIT_00[1-3]|EXCEPTION_\d{3}|DELIVERED_\d{3}/
        const { estimatedDeliveryDate = '', events = [], courier = '' } = responseJson
        const selectedEvents = events?.filter((event: IDeliveryTrackingEvent) => supportedEventsRegex.test(event.key))
        setDeliveryInfo({
            courier,
            estimatedDeliveryDate,
            deliveryTrackingData: selectedEvents,
            isFetchComplete: true,
        })
    }

    const handleWithError = (error: Response) => {
        setDeliveryInfo({
            courier: '',
            estimatedDeliveryDate: '',
            deliveryTrackingData: [],
            isFetchComplete: true,
        })
        reportToRaygun(error)
    }

    useEffect(() => {
        async function fetchDeliveryTrackingAPI() {
            const request = DeliveryTracking.getTrackingDetails({
                baseUrl,
                orderId: orderId.toUpperCase(),
                trackingNumber,
                brand:brand?.toLowerCase()
            })

            await timeout(fetch(request), 30000)
                .then((res) => res.json().then((json) => handleWithResponse(json)))
                .catch((error) => handleWithError(error))
        }

        if (isTrackableOrder) {
           fetchDeliveryTrackingAPI()
        }
    }, [isTrackableOrder])

    return deliveryInfo
}

export default useDeliveryTracking


export enum deliveryCategory {
    DELIVERY = 'DELIVERY',
}